import i18n from "@/i18n";
import { QrCode, Table } from "@/types/globalTypes";
import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { IoMdClose } from "react-icons/io";
import { useSelector } from "react-redux";

type TableEditModalProps = {
  setIsEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  table: Table;
  floorNumber: number | string
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>
};


function TableEditModal({ setIsEditModalOpen, table, floorNumber, setNeedRefetch }: TableEditModalProps) {
  const { t } = useTranslation();
  const lang = i18n.language;
  const token = useSelector((state: { auth: { token: string } }) => state.auth.token);
  const baseUrl = import.meta.env.VITE_BASE_URL
  const [tableNumber, setTableNumber] = useState<string | number>("")
  const [chairsCount, setchairsCount] = useState<string | number>("")
  const [confirmTableDelete, setConfirmTableDelete] = useState(false)
  const [QR, setQR] = useState<QrCode[]>();
  const [selectedQR, setSelectedQR] = useState("");
  function toggleConfirmDelete() {
    setConfirmTableDelete(!confirmTableDelete)
  }
  async function editTable(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const toastId = toast.loading(("tableEdit.editing") as string)
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/floors/${table.floorId}/tables/${table._id}?locale=${lang}`,
        method: "PUT",
        headers: {
          Authorization: token,
        },
        data: {
          number: tableNumber,
          chairsCount: chairsCount,
          qrcode: selectedQR === "" ? undefined : selectedQR

        }
      };
      const response = await axios.request(options);
      if (response.data.success == true) {
        toast.success(t("tableEdit.editedSuccess") as string)
        setNeedRefetch(true)
      }
    } catch (error) {
      console.error(error);
      toast.error(t("tableEdit.error") as string)
    } finally {
      toast.dismiss(toastId)
    }
  }
  async function deleteTable() {

    const toastId = toast.loading(t("tableEdit.deleting") as string)
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/floors/${table.floorId}/tables/${table._id}?locale=${lang}`,
        method: "DELETE",
        headers: {
          Authorization: token,
        }
      };
      const response = await axios.request(options);
      if (response.data.success == true) {
        toast.success(t("tableEdit.deletedSuccess") as string)
        toggleConfirmDelete()
        setIsEditModalOpen(false)
        setNeedRefetch(true)

      }
    } catch (error) {
      console.error(error);
      toast.error(t("tableEdit.error") as string)
    } finally {
      toast.dismiss(toastId)
    }
  }
  async function getQr() {
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/qr-codes?filter=unlinked&locale=${lang}`,
        method: "GET",
        headers: {
          Authorization: token,
        },
      };

      const response = await axios.request(options);
      if (response.data.success === true) {
        const unlinkedQRs: QrCode[] = response.data.qrcodes;

        // Include the table's current QR code if it's not in the list
        const currentQR = table.qrcode;
        if (currentQR && !unlinkedQRs.find((qr) => qr._id === currentQR._id)) {
          unlinkedQRs.unshift(currentQR); // Add it at the top
        }

        setQR(unlinkedQRs);
      }
    } catch (error) {
      console.error(error);
      toast.error(t("tableEdit.error") as string);
    }
  }

  useEffect(() => {
    getQr()
    setTableNumber(table.number)
    setchairsCount(table.chairsCount)
    setSelectedQR(table.qrcode ? table.qrcode._id : "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <>
      {confirmTableDelete && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50">
          <div className="flex w-[95%] max-w-3xl flex-col gap-3 rounded-lg p-5 bg-white shadow-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t("tableEdit.deleteTable")} {table.number}</h2>
              <button
                type="button"
                onClick={toggleConfirmDelete}
                className="text-gray-600 hover:text-gray-800"
              >
                <IoMdClose size={24} className="text-red-600" />
              </button>
            </div>
            <p className="text-gray-600">
              {t("tableEdit.confirmDelete")} {t("tableEdit.table")} {table.number}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={toggleConfirmDelete}
                className="flex items-center justify-center gap-2 rounded-md bg-sidebar text-skyblue   px-3 py-1 text-lg font-semibold  md:gap-3"
              >
                <span>{t("tableEdit.cancel")}</span>
              </button>
              <button
                type="button"
                onClick={deleteTable}
                className="flex items-center justify-center gap-2 rounded-md   bg-red-600 px-3 py-1 text-lg font-semibold text-white md:gap-3"
              >
                <span> {t("tableEdit.delete")}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-[70px]"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex w-[95%] max-w-3xl flex-col gap-3 rounded-lg bg-white shadow-lg">
          <div className="flex items-center justify-between px-2 py-3 sm:px-4 sm:py-4">
            <div className="flex items-center gap-3 text-sm font-semibold sm:text-base md:text-2xl">
              <h2 className="text-lg font-semibold">
                {t("tableEdit.editTable")}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => setIsEditModalOpen(false)}
              className="mb-3 text-gray-600 hover:text-gray-800"
            >
              <IoMdClose size={24} className="text-red-600" />
            </button>
          </div>

          {/* start body */}
          <form
            onSubmit={editTable}
            id="edit-table-form"
            className="thin-scrollbar flex max-h-[55dvh] flex-col gap-5 overflow-y-auto px-3 py-3 sm:px-5 sm:py-4"
          >
            <div className=" p-5">

              <div className="flex gap-3 items-center pb-5 mb-5 border-b">
                <div className="flex items-center gap-3 border-2 border-sidebar font-bold py-1 px-2 rounded-md">
                  {t("tableEdit.floorNumber")} <span className=" h-7 w-7 rounded-full bg-sidebar text-skyblue flex items-center justify-center">{floorNumber}</span>
                </div>
              </div>
              <div className="flex items-center flex-wrap gap-y-2 justify-between">
                <div className="flex gap-3 items-center">
                  <label>{t("tableEdit.tableNumber")} :</label>
                  <input required value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} type="text" className="border-2 w-14 px-2 py-1 rounded-md" />
                </div>
                <div className="flex gap-3 items-center">
                  <label>{t("tableEdit.numberOfChairs")} :</label>
                  <input required value={chairsCount} onChange={(e) => setchairsCount(e.target.value)} type="number" className="border-2 w-14 px-2 py-1 rounded-md" />
                </div>
                <div className="flex gap-3 items-center">
                  <label>{t("tableEdit.qrNumber")} :</label>
                  <div className="border-2 px-2 py-1 rounded-md">
                    <select value={selectedQR} onChange={(e) => setSelectedQR(e.target.value)}>
                      <option value="">{t("tableEdit.selectQr")}</option>
                      {QR?.map((qr) => (
                        <option key={qr._id} value={qr._id}>
                          {qr.serial}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-5 mt-10 justify-center">
                <button type="submit" className="bg-sidebar text-skyblue rounded-md w-fit px-3 py-1">{t("tableEdit.editTable")}</button>
                <button onClick={toggleConfirmDelete} type="button" className="bg-red-600 text-white rounded-md w-fit px-3 py-1">{t("tableEdit.deleteTable")}</button>
              </div>
            </div>
          </form>



          {/* end body */}


        </div>
      </div >
    </>
  );
}

export default TableEditModal;
